// frontend/src/components/common/LocationPicker.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  MapPin, 
  ChevronDown, 
  X, 
  Search,
  CheckCircle,
  Loader
} from 'lucide-react';
import { NIGERIAN_STATES, getLGAsByState } from '../../config/nigerianLocations';

const LocationPicker = ({
  selectedState,
  selectedCity,
  onStateChange,
  onCityChange,
  stateError,
  cityError,
  label = 'Location',
  required = true,
  className = '',
}) => {
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [filteredStates, setFilteredStates] = useState(NIGERIAN_STATES);
  const [filteredCities, setFilteredCities] = useState([]);
  const [isStateFocused, setIsStateFocused] = useState(false);
  const [isCityFocused, setIsCityFocused] = useState(false);

  const stateRef = useRef(null);
  const cityRef = useRef(null);

  // Get LGAs for selected state
  const getLGAs = (stateName) => {
    return getLGAsByState(stateName);
  };

  // Update filtered states on search
  useEffect(() => {
    if (stateSearch.trim()) {
      const lowerQuery = stateSearch.toLowerCase();
      const filtered = NIGERIAN_STATES.filter(state =>
        state.name.toLowerCase().includes(lowerQuery)
      );
      setFilteredStates(filtered);
    } else {
      setFilteredStates(NIGERIAN_STATES);
    }
  }, [stateSearch]);

  // Update filtered cities on search
  useEffect(() => {
    if (selectedState) {
      const lgas = getLGAs(selectedState);
      if (citySearch.trim()) {
        const lowerQuery = citySearch.toLowerCase();
        const filtered = lgas.filter(lga =>
          lga.toLowerCase().includes(lowerQuery)
        );
        setFilteredCities(filtered);
      } else {
        setFilteredCities(lgas);
      }
    } else {
      setFilteredCities([]);
    }
  }, [selectedState, citySearch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStateSelect = (stateName) => {
    onStateChange(stateName);
    onCityChange('');
    setShowStateDropdown(false);
    setStateSearch('');
    setCitySearch('');
    setIsStateFocused(false);
  };

  const handleCitySelect = (cityName) => {
    onCityChange(cityName);
    setShowCityDropdown(false);
    setCitySearch('');
    setIsCityFocused(false);
  };

  const clearSelection = () => {
    onStateChange('');
    onCityChange('');
    setStateSearch('');
    setCitySearch('');
    setShowStateDropdown(false);
    setShowCityDropdown(false);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-xs font-medium text-gray-600">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* State Dropdown */}
      <div ref={stateRef} className="relative">
        <div
          className={`flex items-center justify-between px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm cursor-pointer focus:outline-none transition-all ${
            stateError ? 'ring-2 ring-red-500' : ''
          } ${isStateFocused ? 'ring-2 ring-primary-500/50' : ''}`}
          onClick={() => {
            setShowStateDropdown(!showStateDropdown);
            setShowCityDropdown(false);
          }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className={`truncate ${selectedState ? 'text-gray-800' : 'text-gray-500'}`}>
              {selectedState || 'Select State'}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedState && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
                className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showStateDropdown ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {stateError && (
          <p className="mt-1 text-xs text-red-600">{stateError}</p>
        )}

        {/* State Dropdown Menu */}
        {showStateDropdown && (
          <div className="absolute z-10 mt-1 w-full bg-white rounded-xl shadow-neomorph border border-gray-200 max-h-60 overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  placeholder="Search states..."
                  className="w-full pl-9 pr-3 py-2 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-48">
              {filteredStates.length > 0 ? (
                filteredStates.map((state) => (
                  <button
                    key={state.id}
                    type="button"
                    onClick={() => handleStateSelect(state.name)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50 flex items-center justify-between transition-colors"
                  >
                    <span className="text-gray-700">{state.name}</span>
                    {selectedState === state.name && (
                      <CheckCircle className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  No states found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* City/LGA Dropdown */}
      <div ref={cityRef} className="relative">
        <div
          className={`flex items-center justify-between px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm cursor-pointer focus:outline-none transition-all ${
            !selectedState ? 'opacity-50 cursor-not-allowed' : ''
          } ${cityError ? 'ring-2 ring-red-500' : ''} ${isCityFocused ? 'ring-2 ring-primary-500/50' : ''}`}
          onClick={() => {
            if (selectedState) {
              setShowCityDropdown(!showCityDropdown);
              setShowStateDropdown(false);
            }
          }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className={`truncate ${selectedCity ? 'text-gray-800' : 'text-gray-500'}`}>
              {selectedCity || (selectedState ? 'Select LGA' : 'Select state first')}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedCity && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCityChange('');
                }}
                className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showCityDropdown ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {cityError && (
          <p className="mt-1 text-xs text-red-600">{cityError}</p>
        )}

        {/* City Dropdown Menu */}
        {showCityDropdown && selectedState && (
          <div className="absolute z-10 mt-1 w-full bg-white rounded-xl shadow-neomorph border border-gray-200 max-h-60 overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  placeholder={`Search LGAs in ${selectedState}...`}
                  className="w-full pl-9 pr-3 py-2 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-48">
              {filteredCities.length > 0 ? (
                filteredCities.map((city) => (
                  <button
                    key={city}
                    type="button"
                    onClick={() => handleCitySelect(city)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50 flex items-center justify-between transition-colors"
                  >
                    <span className="text-gray-700">{city}</span>
                    {selectedCity === city && (
                      <CheckCircle className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-sm text-gray-500">
                  No LGAs found for {selectedState}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default LocationPicker;